import React, { useState, useEffect } from 'react';
import styled, { css, keyframes } from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import { Sliders, Zap } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';

/* ---- palette ---- */
const C = {
  bg: '#FFFDF7',
  darkBrown: '#1A1208',
  amber: '#7C3A00',
  caramel: '#C07020',
  text: '#C8A882',
};

/* ---- data ---- */
const PRODUCTS = {
  left: {
    id: 'left',
    label: { en: 'Envinada', es: 'Envinada' },
    title: { en: 'Cajeta Envinada', es: 'Cajeta Envinada' },
    description: {
      en: "A bold twist on tradition. Our classic caramel enriched with a touch of natural flavor — smooth, rich, and unforgettable. Crafted with 100% cow's milk, no preservatives.",
      es: 'Un giro audaz a la tradición. Nuestro caramelo clásico enriquecido con un toque de sabor natural — suave, intenso e inolvidable. Elaborado con 100% leche de vaca, sin conservadores.',
    },
    image: '/images/CAJ1500X500.png',
    glowColor: 'rgba(192,112,32,0.25)',
    barColor: '#C07020',
    badgeColor: '#C07020',
    connectionStatus: { en: 'Natural Flavor', es: 'Sabor Natural' },
    calories: { en: '130 cal per serving', es: '130 cal por porción' },
    features: [
      { label: { en: 'Serving Size', es: 'Porción' }, value: 42, Icon: Sliders },
      { label: { en: 'Added Sugars', es: 'Azúcares añadidos' }, value: 20, Icon: Zap },
    ],
  },
  right: {
    id: 'right',
    label: { en: 'Natural', es: 'Natural' },
    title: { en: 'Cajeta Natural', es: 'Cajeta Natural' },
    description: {
      en: "Pure tradition in every jar. Our original recipe made with 100% cow's milk, sugar, and glucose syrup. No preservatives, no shortcuts — just authentic Mexican caramel since 1950.",
      es: 'Tradición pura en cada frasco. Nuestra receta original con 100% leche de vaca, azúcar y jarabe de glucosa. Sin conservadores, sin atajos — solo auténtica cajeta mexicana desde 1950.',
    },
    image: '/images/CAJ2500X500.png',
    glowColor: 'rgba(124,58,0,0.28)',
    barColor: '#7C3A00',
    badgeColor: '#7C3A00',
    connectionStatus: { en: 'No Preservatives', es: 'Sin Conservadores' },
    calories: { en: '130 cal per serving', es: '130 cal por porción' },
    features: [
      { label: { en: 'Serving Size', es: 'Porción' }, value: 42, Icon: Sliders },
      { label: { en: 'Total Sugars', es: 'Azúcares totales' }, value: 21, Icon: Zap },
    ],
  },
};

/* ---- styled ---- */
const Section = styled.section`
  position: relative;
  min-height: 100vh;
  width: 100%;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding-bottom: 128px;
  background: ${C.bg};
`;

const BgGradient = styled.div`
  position: absolute;
  inset: 0;
  pointer-events: none;
  transition: background 1.2s ease;
  background: ${({ $isLeft }) =>
    $isLeft
      ? `radial-gradient(ellipse 55% 65% at 38% 50%, rgba(192,112,32,0.2) 0%, transparent 70%)`
      : `radial-gradient(ellipse 55% 65% at 62% 50%, rgba(124,58,0,0.22) 0%, transparent 70%)`};
`;

const Inner = styled.div`
  position: relative;
  z-index: 10;
  width: 100%;
  max-width: 1280px;
  padding: 32px 24px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
`;

const Row = styled(motion.div)`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 48px;
  width: 100%;
  @media (min-width: 768px) {
    flex-direction: ${({ $isLeft }) => ($isLeft ? 'row' : 'row-reverse')};
    gap: 96px;
    justify-content: center;
  }
`;

/* product visual */
const VisualWrap = styled.div`
  position: relative;
  flex-shrink: 0;
`;

const pulse = keyframes`
  0%, 100% { opacity: 1; } 50% { opacity: 0.4; }
`;

const DashedRing = styled(motion.div)`
  position: absolute;
  inset: -20%;
  border-radius: 50%;
  border: 1px dashed rgba(192,112,32,0.25);
`;

const GlowBlob = styled(motion.div)`
  position: absolute;
  inset: 0;
  border-radius: 50%;
  opacity: 0.4;
  filter: blur(40px);
  background: ${({ $color }) => $color};
`;

const Circle = styled.div`
  position: relative;
  width: clamp(260px, 40vw, 450px);
  height: clamp(260px, 40vw, 450px);
  border-radius: 50%;
  border: 1px solid rgba(192,112,32,0.12);
  background: rgba(0,0,0,0.16);
  backdrop-filter: blur(6px);
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  box-shadow: 0 8px 48px rgba(26,18,8,0.25);
`;

const StatusBadge = styled.div`
  position: absolute;
  bottom: -28px;
  left: 50%;
  transform: translateX(-50%);
  white-space: nowrap;
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 0.7rem;
  text-transform: uppercase;
  letter-spacing: 0.12em;
  padding: 8px 18px;
  border-radius: 999px;
  background: rgba(13,8,0,0.88);
  color: ${C.text};
  border: 1px solid rgba(192,112,32,0.2);
`;

const Dot = styled.span`
  width: 6px; height: 6px;
  border-radius: 50%;
  background: ${({ $color }) => $color};
  animation: ${pulse} 2s ease-in-out infinite;
`;

/* details */
const Details = styled(motion.div)`
  display: flex;
  flex-direction: column;
  align-items: ${({ $isLeft }) => ($isLeft ? 'flex-start' : 'flex-end')};
  text-align: ${({ $isLeft }) => ($isLeft ? 'left' : 'right')};
  max-width: 420px;
  width: 100%;
`;

const Brand = styled.h2`
  font-size: 0.75rem;
  font-weight: 700;
  letter-spacing: 0.2em;
  text-transform: uppercase;
  color: ${C.amber};
  margin-bottom: 8px;
`;

const ProductTitle = styled.h1`
  font-family: 'Cormorant Garamond', serif;
  font-size: clamp(2rem, 5vw, 2.8rem);
  font-weight: 700;
  color: ${C.darkBrown};
  margin-bottom: 8px;
`;

const Desc = styled.p`
  font-size: 0.95rem;
  line-height: 1.7;
  color: ${C.amber};
  margin-bottom: 32px;
  max-width: 340px;
  ${({ $isLeft }) => !$isLeft && 'margin-left: auto;'}
`;

const StatsBox = styled.div`
  width: 100%;
  padding: 24px;
  border-radius: 16px;
  background: rgba(192,112,32,0.12);
  border: 1px solid rgba(192,112,32,0.2);
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const FeatureRow = styled.div`
  display: flex;
  flex-direction: ${({ $isLeft }) => ($isLeft ? 'row' : 'row-reverse')};
  align-items: center;
  justify-content: space-between;
  font-size: 0.82rem;
  margin-bottom: 8px;
  color: ${C.amber};
`;

const BarTrack = styled.div`
  position: relative;
  height: 8px;
  width: 100%;
  border-radius: 999px;
  background: rgba(255,255,255,0.08);
  overflow: hidden;
`;

const BarFill = styled(motion.div)`
  position: absolute;
  top: 0; bottom: 0; left: 0;
  border-radius: 999px;
  background: ${({ $color }) => $color};
  opacity: 0.8;
`;

const Calories = styled.div`
  margin-top: 20px;
  font-size: 0.85rem;
  font-weight: 500;
  color: ${C.text};
  ${({ $isLeft }) => !$isLeft && 'text-align: right;'}
`;

/* switcher */
const SwitcherWrap = styled.div`
  position: absolute;
  bottom: 32px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 30;
  display: flex;
  justify-content: center;
`;

const SwitcherInner = styled(motion.div)`
  display: flex;
  gap: 4px;
  padding: 6px;
  border-radius: 999px;
  background: rgba(13,8,0,0.88);
  backdrop-filter: blur(8px);
  border: 1px solid rgba(192,112,32,0.15);
`;

const SwitchBtn = styled(motion.button)`
  position: relative;
  padding: 0 28px;
  height: 48px;
  border-radius: 999px;
  font-size: 0.875rem;
  font-weight: 500;
  color: ${({ $active }) => ($active ? '#FFFDF7' : C.text)};
  transition: color 0.3s;
  background: ${({ $active }) => ($active ? 'rgba(192,112,32,0.18)' : 'transparent')};
`;

const containerVariants = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { staggerChildren: 0.1, delayChildren: 0.1 } },
  exit: { opacity: 0, transition: { duration: 0.2 } },
};

const itemVariants = {
  hidden: { opacity: 0, y: 20, filter: 'blur(10px)' },
  visible: { opacity: 1, y: 0, filter: 'blur(0px)', transition: { type: 'spring', stiffness: 100, damping: 20 } },
  exit: { opacity: 0, y: -10, filter: 'blur(5px)' },
};

export default function ProductShowcase() {
  const { lang, t } = useLanguage();
  const [active, setActive] = useState('left');
  const product = PRODUCTS[active];
  const isLeft = active === 'left';

  return (
    <Section id="cajeta" aria-label="Productos Cajeta Hacienda Real">
      <BgGradient $isLeft={isLeft} />

      <Inner>
        <motion.div
          layout
          transition={{ type: 'spring', bounce: 0, duration: 0.9 }}
          style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: '100%' }}
        >
          <Row layout $isLeft={isLeft}>
            {/* Visual */}
            <VisualWrap>
              <DashedRing
                animate={{ rotate: 360 }}
                transition={{ duration: 20, repeat: Infinity, ease: 'linear' }}
              />
              <GlowBlob
                $color={product.glowColor}
                animate={{ scale: [1, 1.08, 1] }}
                transition={{ duration: 4, repeat: Infinity, ease: 'easeInOut' }}
              />
              <Circle>
                <motion.div
                  animate={{ y: [-10, 10, -10] }}
                  transition={{ repeat: Infinity, duration: 6, ease: 'easeInOut' }}
                  style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                >
                  <AnimatePresence mode="wait">
                    <motion.img
                      key={product.id}
                      src={product.image}
                      alt={product.title[lang]}
                      initial={{ opacity: 0, scale: 1.4, filter: 'blur(15px)', rotate: isLeft ? -30 : 30 }}
                      animate={{ opacity: 1, scale: 1, filter: 'blur(0px)', rotate: 0 }}
                      exit={{ opacity: 0, scale: 0.6, filter: 'blur(20px)' }}
                      transition={{ type: 'spring', stiffness: 260, damping: 20 }}
                      style={{ width: '90%', height: '90%', objectFit: 'contain' }}
                      draggable={false}
                    />
                  </AnimatePresence>
                </motion.div>
              </Circle>
              <StatusBadge>
                <Dot $color={product.badgeColor} />
                {product.connectionStatus[lang]}
              </StatusBadge>
            </VisualWrap>

            {/* Details */}
            <AnimatePresence mode="wait">
              <Details
                key={active}
                $isLeft={isLeft}
                variants={containerVariants}
                initial="hidden"
                animate="visible"
                exit="exit"
              >
                <motion.div variants={itemVariants}>
                  <Brand>HACIENDA REAL</Brand>
                  <ProductTitle>{product.title[lang]}</ProductTitle>
                </motion.div>
                <motion.div variants={itemVariants} style={{ width: '100%' }}>
                  <Desc $isLeft={isLeft}>{product.description[lang]}</Desc>
                </motion.div>

                <motion.div variants={itemVariants} style={{ width: '100%' }}>
                  <StatsBox>
                    {product.features.map((feat, i) => (
                      <div key={feat.label.en}>
                        <FeatureRow $isLeft={isLeft}>
                          <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                            <feat.Icon size={14} />
                            {feat.label[lang]}
                          </span>
                          <span style={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>{feat.value}</span>
                        </FeatureRow>
                        <BarTrack>
                          <BarFill
                            $color={product.barColor}
                            initial={{ width: 0 }}
                            animate={{ width: `${Math.min(feat.value, 100)}%` }}
                            transition={{ duration: 1, delay: 0.4 + i * 0.15 }}
                          />
                        </BarTrack>
                      </div>
                    ))}
                  </StatsBox>
                </motion.div>

                <motion.div variants={itemVariants} style={{ width: '100%' }}>
                  <Calories $isLeft={isLeft}>{product.calories[lang]}</Calories>
                </motion.div>
              </Details>
            </AnimatePresence>
          </Row>
        </motion.div>
      </Inner>

      {/* Switcher */}
      <SwitcherWrap>
        <SwitcherInner layout>
          {Object.values(PRODUCTS).map((p) => (
            <SwitchBtn
              key={p.id}
              $active={active === p.id}
              onClick={() => setActive(p.id)}
              whileTap={{ scale: 0.96 }}
            >
              {p.label[lang]}
            </SwitchBtn>
          ))}
        </SwitcherInner>
      </SwitcherWrap>
    </Section>
  );
}
